// frontend/src/components/client/ClientMaintenanceRequests.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Wrench, Calendar, X, CheckCircle, Clock } from 'lucide-react';
import { getMaintenanceRequests, createMaintenanceRequest, cancelMaintenanceRequest, getMyAircraft } from '../../services/clientApi';

const StatusBadge = ({ status }) => {
  const config = {
    pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
    approved: { label: 'Approved', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
    completed: { label: 'Completed', color: 'bg-blue-500/20 text-blue-400', icon: CheckCircle },
    cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400', icon: X }
  };
  const { label, color, icon: Icon } = config[status] || config.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const config = {
    normal: { label: 'Normal', color: 'bg-gray-500/20 text-gray-400' },
    high: { label: 'High', color: 'bg-orange-500/20 text-orange-400' },
    urgent: { label: 'Urgent', color: 'bg-red-500/20 text-red-400' }
  };
  const { label, color } = config[priority] || config.normal;
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>{label}</span>;
};

const ClientMaintenanceRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [aircraftList, setAircraftList] = useState([]);
  const [formData, setFormData] = useState({ aircraft_id: '', scheduled_date: '', description: '', priority: 'normal' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadRequests(); loadAircraft(); }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const response = await getMaintenanceRequests();
      setRequests(response.data);
    } catch (error) { console.error('Error loading maintenance requests:', error); }
    finally { setLoading(false); }
  };

  const loadAircraft = async () => {
    try {
      const response = await getMyAircraft();
      setAircraftList(response.data);
    } catch (error) { console.error('Error loading aircraft:', error); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.aircraft_id || !formData.scheduled_date || !formData.description) {
      alert('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await createMaintenanceRequest(formData);
      setShowModal(false);
      setFormData({ aircraft_id: '', scheduled_date: '', description: '', priority: 'normal' });
      await loadRequests();
    } catch (error) {
      alert('Error creating maintenance request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    if (window.confirm('Cancel this maintenance request?')) {
      try {
        await cancelMaintenanceRequest(id);
        await loadRequests();
      } catch (error) { alert('Error cancelling request'); }
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="px-4 sm:px-0">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0 mr-3">
          <h2 className="text-xl font-bold text-white">Maintenance Requests</h2>
          <p className="text-gray-400 text-sm">Schedule and track aircraft maintenance</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="p-2.5 sm:px-4 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-lg text-white transition flex items-center gap-2 flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">New Request</span>
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700">
          <Wrench className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">No maintenance requests yet</p>
          <button onClick={() => setShowModal(true)} className="mt-3 text-blue-400 hover:text-blue-300 active:opacity-70 text-sm">
            Create a request
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <div key={req.id} className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg flex-shrink-0">
                  <Wrench className="w-5 h-5 text-red-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                      <span className="text-white font-medium">{req.tail_number}</span>
                      <PriorityBadge priority={req.priority} />
                      <StatusBadge status={req.status} />
                    </div>
                    {req.status === 'pending' && (
                      <button
                        onClick={() => handleCancel(req.id)}
                        className="p-1.5 hover:bg-red-500/20 active:bg-red-500/30 rounded-lg text-red-400 transition flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-gray-400 mt-1 line-clamp-2">{req.description}</p>
                  <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                    <Calendar className="w-3 h-3 flex-shrink-0" />
                    <span>{formatDate(req.scheduled_date)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FAB for mobile */}
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-6 right-6 sm:hidden w-14 h-14 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-full text-white shadow-lg flex items-center justify-center transition z-40"
        style={{ boxShadow: '0 4px 20px rgba(59,130,246,0.5)' }}
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Modal — slides up from bottom on mobile */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-50">
          <div className="bg-gray-800 rounded-t-2xl sm:rounded-xl w-full sm:max-w-md border border-gray-700 max-h-[92vh] overflow-y-auto">
            <div className="flex justify-center pt-3 pb-1 sm:hidden">
              <div className="w-10 h-1 bg-gray-600 rounded-full" />
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
              <h3 className="text-white font-semibold">New Maintenance Request</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Aircraft *</label>
                <select
                  value={formData.aircraft_id}
                  onChange={(e) => setFormData({ ...formData, aircraft_id: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base"
                  required
                >
                  <option value="">Select an aircraft</option>
                  {aircraftList.map((a) => (
                    <option key={a.id} value={a.id}>{a.tail_number} – {a.manufacturer} {a.model}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base"
                >
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Preferred Date/Time *</label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white resize-none text-base"
                  placeholder="Describe the maintenance needed..."
                  required
                />
              </div>
              <div className="flex flex-col gap-3 pt-2 pb-2">
                <button type="submit" disabled={submitting}
                  className="w-full py-3.5 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-xl text-white font-semibold disabled:opacity-50 transition text-base">
                  {submitting ? 'Sending...' : 'Submit Request'}
                </button>
                <button type="button" onClick={() => setShowModal(false)}
                  className="w-full py-3.5 bg-gray-700 hover:bg-gray-600 active:bg-gray-500 rounded-xl text-white transition text-base">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientMaintenanceRequests;